import { Checkbox, FormControlLabel, FormGroup, FormLabel, MenuItem, Stack } from '@mui/material';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';

import RoundedSelect from 'common/components/RoundedSelect';
import UserRole from 'common/enums/roles';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useSelector } from 'react-redux';
import { getUser } from 'redux/slices/authSlice';
import { getServicesSavePending } from 'redux/slices/iCalendarV4';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => {
    return {
        root: {
            maxWidth: theme.spacing(140),
            width: '70vw',
            [theme.breakpoints.up('xl')]: {
                maxHeight: theme.spacing(160),
                maxWidth: theme.spacing(140),
                width: '55vw',
            },
            [theme.breakpoints.down('lg')]: {
                width: '80vw',
            },
            [theme.breakpoints.down('mg')]: {
                width: '50vw',
            },
            [theme.breakpoints.down('md')]: {
                width: '70vw',
            },
            [theme.breakpoints.down('ms')]: {
                width: '80vw',
            },
        },
        mW12: {
            minWidth: theme.spacing(14),
        },
    };
});

const ServiceFormBookingOptions = (props) => {
    const { isValidForm, setStep, step, fields, handleFieldChange, cancel } = props;
    const { classes } = useStyles();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const timeZone = useSettingValue(SettingsKey.TIMEZONE) || 'Europe/Madrid';
    const savePending = useSelector(getServicesSavePending);
    const user = useSelector(getUser);

    const isAuthorize = useAuthLicenseAddOns();
    const iCall = isAuthorize(['oneToOne.iCall']);

    const incrementTimeArray = useMemo(() => [5, 10, 15, 20, 30, 60], []);

    const handleNextStep = () => {
        if (isValidForm()) {
            setStep(step + 1);
        } else {
            enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
        }
    };

    return (
        <ValidatorForm
            onSubmit={handleNextStep}
            onError={(e) => {
                enqueueSnackbar(t('app.common.formError'), { variant: 'error' });
            }}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
        >
            <Stack direction="column" className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}>
                <Stack direction="column" spacing={3}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="active"
                                    className={cx(flexBox.padding0, flexBox.margin01)}
                                    checked={fields.active}
                                    onChange={handleFieldChange}
                                    inputProps={{ 'aria-label': 'disabled checkbox' }}
                                />
                            }
                            label={<FormLabel>{t('service.form.active')}</FormLabel>}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="allowGuestMultipleBookings"
                                    className={cx(flexBox.padding0, flexBox.margin01)}
                                    checked={fields.allowGuestMultipleBookings}
                                    onChange={handleFieldChange}
                                    inputProps={{ 'aria-label': 'disabled checkbox' }}
                                />
                            }
                            label={<FormLabel>{t('service.form.allowMultipleAppointment')}</FormLabel>}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="allowAgentOverlappingBookings"
                                    className={cx(flexBox.padding0, flexBox.margin01)}
                                    checked={fields.allowAgentOverlappingBookings}
                                    onChange={handleFieldChange}
                                    inputProps={{ 'aria-label': 'disabled checkbox' }}
                                />
                            }
                            label={<FormLabel>{t('service.form.allowOverlapping')}</FormLabel>}
                        />
                    </FormGroup>

                    {iCall && (user.role === UserRole.ROOT || user.role === UserRole.ADMIN) ? (
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="enableVideoCall"
                                        className={cx(flexBox.padding0, flexBox.margin01)}
                                        checked={fields.enableVideoCall}
                                        onChange={handleFieldChange}
                                        inputProps={{ 'aria-label': 'disabled checkbox' }}
                                    />
                                }
                                label={<FormLabel>{t('service.form.hasICall')}</FormLabel>}
                            />
                        </FormGroup>
                    ) : null}

                    <FormGroup>
                        <FormLabel>{`${t('common.term.Duration')} (${t('common.term.minutes')})`}</FormLabel>
                        <CustomTextValidator
                            name="bookingDurationMinutes"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.bookingDurationMinutes}
                            onChange={handleFieldChange}
                            type="number"
                            InputProps={{
                                inputProps: { min: 1, max: 60, step: 1 },
                            }}
                            validators={['required']}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{`${t('calendar.timeBetweenEvents')} (${t('common.term.minutes')})`}</FormLabel>
                        <CustomTextValidator
                            name="bookingBufferTimeMinutes"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.bookingBufferTimeMinutes}
                            onChange={handleFieldChange}
                            type="number"
                            InputProps={{
                                inputProps: { min: 0, max: 60, step: 5 },
                            }}
                            validators={['required']}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{`${t('calendar.minimumTimeToSchedule')} (${t('common.term.Days')})`}</FormLabel>
                        <CustomTextValidator
                            name="minAdvanceBookingMinutes"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.minAdvanceBookingMinutes}
                            onChange={handleFieldChange}
                            type="number"
                            InputProps={{
                                inputProps: { min: 0, max: 30, step: 1 },
                            }}
                            validators={['required']}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{`${t('calendar.maximumTimeToSchedule')} (${t('common.term.Days')})`}</FormLabel>
                        <CustomTextValidator
                            name="maxAdvanceBookingMinutes"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.maxAdvanceBookingMinutes}
                            onChange={handleFieldChange}
                            type="number"
                            InputProps={{
                                inputProps: { min: 0, step: 1 },
                            }}
                            validators={['required']}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{`${t('calendar.timeIncrementation')} (${t('common.term.minutes')})`}</FormLabel>
                        <RoundedSelect
                            bold={true}
                            size="big"
                            fullWidth={true}
                            name="slotIntervalMinutes"
                            value={fields.slotIntervalMinutes}
                            onChange={handleFieldChange}
                        >
                            {incrementTimeArray.map((time) => (
                                <MenuItem key={time} value={time}>
                                    {time}
                                </MenuItem>
                            ))}
                        </RoundedSelect>
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{`${t('calendar.slotStartOffset')} (${t('common.term.minutes')})`}</FormLabel>
                        <CustomTextValidator
                            name="slotStartOffsetMinutes"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.slotStartOffsetMinutes}
                            onChange={handleFieldChange}
                            type="number"
                            InputProps={{
                                inputProps: { min: 0, max: 59, step: 1 },
                            }}
                            validators={['required']}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>{t('calendar.bookingUntil')}</FormLabel>
                        <CustomTextValidator
                            name="bookingCutoffDate"
                            className={classes.mW12}
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={
                                fields.bookingCutoffDate
                                    ? moment.tz(fields.bookingCutoffDate, timeZone).format('YYYY-MM-DDTHH:mm')
                                    : undefined
                            }
                            onChange={handleFieldChange}
                            type={'datetime-local'}
                            InputProps={{
                                inputProps: {
                                    shrink: true,
                                    min: moment.tz(timeZone).format('YYYY-MM-DDTHH:mm'),
                                },
                            }}
                        />
                    </FormGroup>

                    {/* <FormGroup>
                        <FormLabel>{`${t('calendar.slotCapacity')}`}</FormLabel>
                        <CustomTextValidator
                            name="slotCapacity"
                            fullWidth={true}
                            variant="outlined"
                            size="small"
                            value={fields.slotCapacity}
                            onChange={handleFieldChange}
                            type="number"
                            disabled={fields.allowOverlapping && !selectedUnits?.length}
                            InputProps={{
                                inputProps: { min: 1, step: 1 },
                            }}
                        />
                        {fields.allowOverlapping && selectedUnits.length === 0 && (
                            <FormHelperText>{t('calendar.slotCapacityDisabledMessage')}</FormHelperText>
                        )}
                    </FormGroup> */}
                </Stack>
            </Stack>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth disabled={savePending}>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    type="submit"
                    disabled={savePending}
                    loading={savePending}
                >
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default ServiceFormBookingOptions;
